import { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { generateCSSFilters, getImageUrl, showImageWhenLoaded } from '../common/Media';
import { AppContext } from '../contexts/AppContext';
import useFetchMedia from '../hooks/useFetchMedia';
import { MEDIA_PREFIX } from '../Settings';

interface BackgroundImageProps {
  media?: any,
  backgroundColor?: string
}

export const ImageElement = styled.img<{ $focalX?: number, $focalY?: number, $filters?: string, $backgroundColor?: string | null }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ $focalX = 50, $focalY = 50 }) => `${$focalX}% ${$focalY}%`};
  filter: ${({ $filters }) => $filters ?? "none"};
  perspective: 1000;
  backface-visibility: hidden;
  transform: translateY(0);
  ${({ $backgroundColor }) => $backgroundColor && `background-color: ${$backgroundColor};`}
`;


const StaticBackground = styled.div<{ $backgroundColor?: string | null }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  perspective: 1000;
  backface-visibility: hidden;
  transform: translateY(0);
  ${({ $backgroundColor }) => $backgroundColor && `background-color: ${$backgroundColor};`}
`;

/**
 * Render a background image. Used by most views.
 * @returns {JSX.Element} Component template
 */
const BackgroundImage: FC<BackgroundImageProps> = ({media, backgroundColor}) => {
  const { previewIsActive } = useContext(AppContext)
  const [backgroundImage] = useFetchMedia(media, { dimension: "3000" });
  const [filterCss, setFilterCss] = useState<string | undefined>(undefined);

  // Parse CSS filters from media object
  useEffect(() => {
    if (media?.filters) {
      setFilterCss(generateCSSFilters(media?.filters));
    }
  }, [media]);

  if (backgroundImage?.url) {
    return <ImageElement className={previewIsActive ? "" : "loading"} onLoad={showImageWhenLoaded} src={`${MEDIA_PREFIX}${getImageUrl(backgroundImage?.url)}`} $focalX={backgroundImage?.focalPoint?.x} $focalY={backgroundImage?.focalPoint?.y} $filters={filterCss} $backgroundColor={backgroundColor}/>
  } else if (backgroundColor) {
    return <StaticBackground $backgroundColor={backgroundColor}/> 
  } else {
    return null;
  }
}

export default BackgroundImage;